
import {
  computed,
  defineComponent, onMounted, reactive, ref, Ref, watch,
} from 'vue';
import { useDriveStore } from '@/modules/drive/store/driveStore';
import AppHeader from './AppHeader.vue';
import AppSearch from './UI/AppSearch.vue';
import AppCaseViewPopUp from '@/components/AppCaseViewPopUp.vue';
import ItemCase from '@/modules/drive/components/items/ItemCase.vue';
import { ICase } from '@/types/interfaces';
import NotificationService from '@/services/NotificationService';
import { PHOTO_VIEW_TYPE } from '@/types/enums';
import AppSkeletonCase from './skeletons/AppSkeletonCase.vue';
import AppSkeletonRect from './skeletons/AppSkeletonRect.vue';
import UsePagination from '@/composables/UsePagination';
import DriveService from '@/modules/drive/services/DriveService';
import { DIRECTION } from '@/helpers/isScrollEnd';

export default defineComponent({
  emits: ['cancel', 'done'],
  props: {
    title: { type: String, default: 'Select Case' },
  },
  components: {
    AppHeader,
    AppSearch,
    AppCaseViewPopUp,
    ItemCase,
    AppSkeletonCase,
    AppSkeletonRect,
  },
  setup(props, { emit }) {
    const driveStore = useDriveStore();

    const searchCases = ref<ICase[]>([]);
    const selectedCase: Ref<ICase | undefined> = ref();

    const previewCase = reactive({
      id: '', title: '', photosAmount: 0, filesAmount: 0, show: false,
    });
    const dataLoading = ref(false);

    const {
      scroller, pageIdx, isPageLoading, onScroll, onCacheScroll, setLastPage,
    } = UsePagination();

    const cases = computed(() => {
      if (driveStore.search) {
        return searchCases.value;
      }
      return driveStore.cases.data;
    });

    function doneAction() {
      if (!selectedCase.value) {
        return;
      }
      emit('done', selectedCase.value);
      // NotificationService.notifySuccess();
    }

    async function fetchCacheData() {
      return driveStore.fetchCases();
    }

    async function fetchSearchData() {
      return DriveService.getCases(pageIdx.value, driveStore.search)
        .then((response) => {
          if (pageIdx.value === 0) {
            searchCases.value = response.results.data.items;
          } else {
            searchCases.value.push(...response.results.data.items);
          }
          return response;
        });
    }

    let timeout: ReturnType<typeof setTimeout>;
    watch(() => driveStore.search, (newSearch, oldSearch) => {
      clearTimeout(timeout);
      pageIdx.value = 0;
      dataLoading.value = !!newSearch;
      timeout = setTimeout(() => {
        fetchSearchData()
          .then((response) => {
            setLastPage(response.results.data.paging.items, response.results.data.paging.itemsOnPage);
          })
          .finally(() => {
            dataLoading.value = false;
          });
      }, 300);
    });

    function setPreviewCase(iCase: ICase) {
      previewCase.id = iCase.id!;
      previewCase.title = iCase.title!;
      previewCase.filesAmount = iCase.filesAmount!;
      previewCase.photosAmount = iCase.photosAmount!;
      previewCase.show = true;
    }

    function selectCase(id: string) {
      selectedCase.value = driveStore.cases.data.find((item) => item.id === id);
      previewCase.show = false;
    }

    function handleScroll() {
      if (driveStore.search) {
        onScroll(fetchSearchData, DIRECTION.BOTTOM, dataLoading.value);
      } else {
        onCacheScroll(fetchCacheData, DIRECTION.BOTTOM, driveStore.cases.lastPage, driveStore.cases.page);
      }
    }

    onMounted(() => {
      driveStore.search = '';
    });

    return {
      driveStore,
      selectedCase,
      isPageLoading,
      dataLoading,
      scroller,
      handleScroll,
      doneAction,
      PHOTO_VIEW_TYPE,
      previewCase,
      setPreviewCase,
      selectCase,
      cases,
    };
  },
});
